import React, { useEffect, useState } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, get, remove, update } from 'firebase/database';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { logAdminAction } from '../../firebase/auditLogger';

function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Security Kick: Only Superadmins can view this page
    if (userProfile && userProfile.role !== 'superadmin') {
      navigate('/admin/dashboard');
      return;
    }

    const fetchAdmins = async () => {
      try {
        const adminsRef = ref(db, 'admins');
        const snapshot = await get(adminsRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          const parsedAdmins = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          }));
          
          // Sort newest first
          parsedAdmins.sort((a, b) => b.createdAt - a.createdAt);
          setAdmins(parsedAdmins);
        } else {
          setAdmins([]);
        }
      } catch (err) {
        console.error("Failed to load administrators", err);
      }
      setLoading(false);
    };

    if (userProfile) {
      fetchAdmins();
    }
  }, [userProfile, navigate]);

  const handleDeleteAdmin = async (adminId, adminName) => {
    if(window.confirm(`CRITICAL: Are you sure you want to permanently delete administrator ${adminName}? They will immediately lose all access.`)) {
      try {
        await remove(ref(db, `admins/${adminId}`));
        setAdmins(prev => prev.filter(a => a.id !== adminId));
        logAdminAction(userProfile.adminId, `Permanently Terminated Administrator Access Credentials for ${adminName}`, adminId);
      } catch (err) {
        alert("Failed to delete admin document: " + err.message);
      }
    }
  };

  const handleApproveAdmin = async (adminId, adminName) => {
    try {
      await update(ref(db, `admins/${adminId}`), { status: 'active' });
      setAdmins(prev => prev.map(a => a.id === adminId ? { ...a, status: 'active' } : a));
      logAdminAction(userProfile.adminId, `Approved and Activated Pending Administrator Account for ${adminName}`, adminId);
    } catch (err) {
      alert("Failed to approve admin: " + err.message);
    }
  };

  const handleDemoteAdmin = async (adminId, currentRole) => {
    let newRole = 'support';
    if (currentRole === 'superadmin') newRole = 'moderator';
    else if (currentRole === 'moderator') newRole = 'support';
    else return alert("Admin is already at lowest tier.");

    if(window.confirm(`Downgrade role to ${newRole}?`)) {
      try {
        await update(ref(db, `admins/${adminId}`), { role: newRole });
        setAdmins(prev => prev.map(a => a.id === adminId ? { ...a, role: newRole } : a));
        logAdminAction(userProfile.adminId, `Downgraded Administrator Role Privileges to ${newRole.toUpperCase()}`, adminId);
      } catch (err) {
        alert("Failed to update role: " + err.message);
      }
    }
  };

  const filteredAdmins = admins.filter(a => 
    a.adminId?.toLowerCase().includes(search.toLowerCase()) || 
    a.email?.toLowerCase().includes(search.toLowerCase()) ||
    a.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (!userProfile || userProfile.role !== 'superadmin') return null; // Prevent flash before redirect

  return (
    <div className="animate-up">
      <div className="admin-page-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div>
          <h1>Administrator Management</h1>
          <p>Superadmin privilege required. View, edit, and revoke system access.</p>
        </div>
        <button onClick={() => navigate('/admin/register')} style={{background: '#00ffae', color: '#0d1117', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'}}>
          <i className="fa-solid fa-plus"></i> New Admin
        </button>
      </div>

      <div className="admin-card" style={{marginBottom: '30px', padding: '15px'}}>
        <div style={{display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '10px 15px', border: '1px solid rgba(255,255,255,0.1)'}}>
          <i className="fa-solid fa-magnifying-glass" style={{color: '#8b949e', marginRight: '15px'}}></i>
          <input 
            type="text" 
            placeholder="Search by AID, Name, or Email..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{background: 'transparent', border: 'none', color: '#fff', outline: 'none', width: '100%', fontSize: '1rem'}}
          />
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#8b949e'}}>Loading admin records...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>AID / Name</th>
                  <th>Contact Email</th>
                  <th>Permission Tier</th>
                  <th>Created Date</th>
                  <th style={{textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAdmins.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{textAlign: 'center', padding: '40px', color: '#8b949e'}}>No administrators found.</td>
                  </tr>
                ) : (
                  filteredAdmins.map(admin => (
                    <tr key={admin.id}>
                      <td>
                        <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                          <div style={{minWidth: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid rgba(255,255,255,0.1)'}}>
                            <i className="fa-solid fa-shield" style={{color: '#c9d1d9'}}></i>
                          </div>
                          <div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                               <strong style={{display: 'block', color: '#fff', fontSize: '1.05rem'}}>{admin.name}</strong>
                               {admin.status === 'pending' && <span style={{fontSize: '0.65rem', background: 'rgba(255,161,22,0.1)', color: '#ffa116', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(255,161,22,0.2)'}}>PENDING</span>}
                            </div>
                            <span style={{fontSize: '0.8rem', color: '#8b949e', fontFamily: 'monospace'}}>{admin.adminId}</span>
                          </div>
                        </div>
                      </td>
                      <td>{admin.email}</td>
                      <td>
                        <span className="status-badge" style={{
                          background: admin.role === 'superadmin' ? 'rgba(255,55,95,0.1)' : admin.role === 'moderator' ? 'rgba(255,161,22,0.1)' : 'rgba(0,255,174,0.1)',
                          color: admin.role === 'superadmin' ? '#ff375f' : admin.role === 'moderator' ? '#ffa116' : '#00ffae',
                          border: `1px solid ${admin.role === 'superadmin' ? 'rgba(255,55,95,0.2)' : admin.role === 'moderator' ? 'rgba(255,161,22,0.2)' : 'rgba(0,255,174,0.2)'}`,
                          display: 'inline-flex', alignItems: 'center', gap: '5px'
                        }}>
                          {admin.role === 'superadmin' && <i className="fa-solid fa-star"></i>}
                          {admin.role === 'moderator' && <i className="fa-solid fa-bolt"></i>}
                          {admin.role === 'support' && <i className="fa-solid fa-headset"></i>}
                          {admin.role ? admin.role.toUpperCase() : 'UNKNOWN'}
                        </span>
                      </td>
                      <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                      <td style={{textAlign: 'right'}}>
                        {admin.status === 'pending' ? (
                          <>
                            <button 
                             onClick={() => handleApproveAdmin(admin.id, admin.name)}
                             title="Approve Access" 
                             style={{background: 'rgba(0,255,174,0.1)', border: '1px solid rgba(0,255,174,0.2)', color: '#00ffae', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', marginRight: '10px'}}
                            >
                              <i className="fa-solid fa-check"></i> Approve
                            </button>
                            <button 
                             onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                             title="Reject Registration" 
                             style={{background: 'rgba(255,55,95,0.1)', border: '1px solid rgba(255,55,95,0.2)', color: '#ff375f', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer'}}
                            >
                              <i className="fa-solid fa-xmark"></i> Reject
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                             onClick={() => handleDemoteAdmin(admin.id, admin.role)}
                             title="Demote Role" 
                             disabled={admin.role === 'support'}
                             style={{background: 'rgba(255,161,22,0.1)', border: '1px solid rgba(255,161,22,0.2)', color: '#ffa116', padding: '8px 12px', borderRadius: '6px', cursor: admin.role === 'support' ? 'not-allowed' : 'pointer', marginRight: '10px', opacity: admin.role === 'support' ? 0.5 : 1}}
                            >
                              <i className="fa-solid fa-arrow-down-wide-short"></i>
                            </button>
                            <button 
                             onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                             title="Revoke Access" 
                             style={{background: 'rgba(255,55,95,0.1)', border: '1px solid rgba(255,55,95,0.2)', color: '#ff375f', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer'}}
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminAdmins;
